import React from 'react';
import type { IconType } from 'react-icons';
import {
  FiClipboard,
  FiCheckCircle,
  FiClock,
  FiAlertTriangle,
  FiSearch,
  FiGrid,
  FiHome,
  FiTarget,
  FiFileText,
  FiCheckSquare,
  FiList,
  FiFile,
  FiHelpCircle,
  FiUsers,
  FiCpu,
  FiCalendar,
  FiGitMerge,
  FiMail,
  FiBarChart2,
  FiSettings,
  FiChevronRight,
  FiChevronDown,
  FiMenu,
  FiUser,
  FiLogOut,
  FiLock,
  FiBox,
  FiShield,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiEye,
  FiEyeOff,
  FiImage,
  FiFilm,
  FiMoreVertical,
  FiRotateCcw,
  FiRotateCw,
  FiSend,
  FiSlash,
  FiCrop,
  FiSliders,
  FiDroplet,
  FiGlobe,
  FiMoon,
  FiSun,
} from 'react-icons/fi';
import { HiOutlineUserGroup, HiOutlineOfficeBuilding } from 'react-icons/hi';
import { MdOutlineWarehouse, MdRateReview } from 'react-icons/md';

type IconProps = { size?: number };

const wrap = (Glyph: IconType, defaultSize: number): React.FC<IconProps> =>
  ({ size = defaultSize }) => <Glyph size={size} aria-hidden="true" />;

export const IconClipboard = wrap(FiClipboard, 22);
export const IconCheck = wrap(FiCheckCircle, 22);
export const IconClock = wrap(FiClock, 22);
export const IconAlert = wrap(FiAlertTriangle, 22);
export const IconSearch = wrap(FiSearch, 16);

/* ---------- Navigation / shell icons ---------- */

export const IconDashboard = wrap(FiGrid, 18);
export const IconHome = wrap(FiHome, 18);
export const IconTrack = wrap(FiTarget, 18);
export const IconReport = wrap(FiFileText, 18);
export const IconTasks = wrap(FiCheckSquare, 18);
export const IconList = wrap(FiList, 18);
export const IconForms = wrap(FiFile, 18);
export const IconHelp = wrap(FiHelpCircle, 18);
export const IconUsers = wrap(FiUsers, 18);
export const IconGroups = wrap(HiOutlineUserGroup, 18);
export const IconAi = wrap(FiCpu, 18);
export const IconSchedule = wrap(FiCalendar, 18);
export const IconWorkflow = wrap(FiGitMerge, 18);
export const IconEnvelope = wrap(FiMail, 18);
export const IconChart = wrap(FiBarChart2, 18);
export const IconReview = wrap(MdRateReview, 18);
export const IconSettings = wrap(FiSettings, 18);
export const IconChevronRight = wrap(FiChevronRight, 16);
export const IconChevronDown = wrap(FiChevronDown, 16);
export const IconMenu = wrap(FiMenu, 20);
export const IconBuilding = wrap(HiOutlineOfficeBuilding, 18);
export const IconUser = wrap(FiUser, 18);
export const IconLogout = wrap(FiLogOut, 18);
export const IconLock = wrap(FiLock, 22);
export const IconBox = wrap(FiBox, 18);
export const IconShield = wrap(FiShield, 18);
export const IconWarehouse = wrap(MdOutlineWarehouse, 18);
export const IconPlus = wrap(FiPlus, 16);
export const IconEdit = wrap(FiEdit2, 16);
export const IconTrash = wrap(FiTrash2, 16);
export const IconClose = wrap(FiX, 18);
export const IconEye = wrap(FiEye, 18);
export const IconEyeOff = wrap(FiEyeOff, 18);
export const IconImage = wrap(FiImage, 18);
export const IconVideo = wrap(FiFilm, 18);
export const IconMoreVertical = wrap(FiMoreVertical, 16);
export const IconRotateCcw = wrap(FiRotateCcw, 16);
export const IconRotateCw = wrap(FiRotateCw, 16);
export const IconSend = wrap(FiSend, 16);
export const IconSlash = wrap(FiSlash, 16);
export const IconCrop = wrap(FiCrop, 16);
export const IconSliders = wrap(FiSliders, 16);
export const IconDroplet = wrap(FiDroplet, 16);
export const IconGlobe = wrap(FiGlobe, 18);
export const IconMoon = wrap(FiMoon, 18);
export const IconSun = wrap(FiSun, 18);

/**
 * Icon registry — maps the string names used in module-config to components.
 * Mirrors webapp2's `iconName` indirection, resolved via react-icons.
 */
export const iconRegistry: Record<string, React.FC<IconProps>> = {
  dashboard: IconDashboard,
  home: IconHome,
  track: IconTrack,
  report: IconReport,
  tasks: IconTasks,
  list: IconList,
  forms: IconForms,
  help: IconHelp,
  users: IconUsers,
  groups: IconGroups,
  ai: IconAi,
  schedule: IconSchedule,
  workflow: IconWorkflow,
  envelope: IconEnvelope,
  chart: IconChart,
  review: IconReview,
  settings: IconSettings,
  building: IconBuilding,
  clipboard: IconClipboard,
  clock: IconClock,
  alert: IconAlert,
  box: IconBox,
  package: IconBox,
  shield: IconShield,
  warehouse: IconWarehouse,
  user: IconUser,
  trash: IconTrash,
  image: IconImage,
  lock: IconLock,
  globe: IconGlobe,
};

export const getIcon = (name?: string): React.FC<IconProps> | undefined =>
  name ? iconRegistry[name] : undefined;
